/**
 * Localized notification titles for the alert surfaces (bell popover + toast /
 * desktop pop-up).
 *
 * Notification `title`/`body` are persisted in English at emit time on the
 * backend, so the inbox would otherwise show English text to a Hebrew/Spanish
 * user. Every event already declares a translatable `i18n_key` in the shared
 * `notification_events.config.json` catalog (the same source the preferences
 * matrix renders from), so the alert surfaces re-derive a localized title from
 * the notification's `notification_type` and fall back to the stored title when
 * no catalog entry exists. Terminology placeholders (`{{Deal}}`, `{{Task}}`, …)
 * resolve automatically via the global i18next `interpolation.defaultVariables`.
 *
 * The `body` (a per-event excerpt — user content like the comment text) is NOT
 * localized; it is shown verbatim.
 */
import type { TFunction } from 'i18next';

import notificationEventsConfig from '../../shared-config/notification_events.config.json';
import { searchScore } from '../utils/searchScore';

interface RawEvent {
  notification_type?: string;
  i18n_key?: string;
}
interface RawCategory {
  events?: RawEvent[];
}
interface RawModule {
  categories?: RawCategory[];
}
interface RawConfig {
  modules?: Record<string, RawModule>;
}

/**
 * Modules whose stored titles are intentionally dynamic — they embed per-event
 * specifics the generic catalog label would drop (e.g. messaging:
 * `"{sender} mentioned you: {preview}"`). Those titles are surfaced verbatim.
 */
const DYNAMIC_TITLE_MODULES = new Set<string>(['messaging']);

/**
 * Same exclusion as `DYNAMIC_TITLE_MODULES`, keyed by notification-type prefix
 * because these events do NOT get a module of their own.
 *
 * A `@reminder_rule` nag's stored title names the thing being chased — "Deal
 * 'Acme' stuck in Negotiation for 5 days", "3 bills overdue" — composed per
 * candidate by the rule's own formatter. Its catalog label is the generic rule
 * name ("Stale {{Deal}}"), which is right for a settings row and useless in a
 * bell popover: it drops the one detail the reader opened the notification for.
 * Reminder events live inside ordinary modules (pipeline, accounting, …) whose
 * other events DO want the catalog label, so the exclusion is per type, not per
 * module. The `reminder.` prefix is a contract, held backend-side by
 * shared/reminders/__tests__/test_catalog_binding.py.
 *
 * `entity_reminder.` is the user-scheduled one-shot ping, and it is here for the
 * same reason one step further along. Its catalog label is "Reminder due" — four
 * words that are true of every reminder ever sent, on a toast that carries no
 * other context — while its stored title names the kind of record, the record
 * itself and (in the body) the note the user typed: "{{Deal}} reminder: Acme Corp
 * renewal". Overriding that with the label discarded the whole message.
 *
 * The trade the two exclusions share: a stored title is composed in English at
 * emit time, so a Hebrew or Spanish reader gets a specific English sentence
 * instead of a generic translated one. That is the better half of a bad choice —
 * the alternative tells them nothing in any language — and the durable fix is the
 * `email_i18n` spec these notifications already carry on `data`, which the email
 * path re-translates per recipient and which the frontend cannot resolve today
 * (its keys live in the BACKEND bundles).
 */
const DYNAMIC_TITLE_TYPE_PREFIXES = ['reminder.', 'entity_reminder.'];

function buildTitleKeyMap(): Record<string, string> {
  const map: Record<string, string> = {};
  const ambiguous = new Set<string>();
  const modules = (notificationEventsConfig as RawConfig).modules ?? {};

  for (const [moduleId, mod] of Object.entries(modules)) {
    if (DYNAMIC_TITLE_MODULES.has(moduleId)) continue;
    for (const category of mod.categories ?? []) {
      for (const event of category.events ?? []) {
        const type = event.notification_type;
        const key = event.i18n_key;
        if (!type || !key) continue;
        if (DYNAMIC_TITLE_TYPE_PREFIXES.some((prefix) => type.startsWith(prefix))) continue;
        if (type in map && map[type] !== key) {
          // Defensive: a notification_type that resolves to two different catalog
          // keys can't be disambiguated from the type alone, so it falls back to
          // the stored title. The first-seen value is written above and removed by
          // the `ambiguous` cleanup below. (No such cross-module collision exists
          // in the current config — `message.new`'s DM/channel split is already
          // dropped by the messaging module exclusion before reaching here.)
          ambiguous.add(type);
          continue;
        }
        map[type] = key;
      }
    }
  }

  for (const type of ambiguous) delete map[type];
  return map;
}

/** `notification_type` → catalog `i18n_key` (unambiguous, non-dynamic only). */
export const NOTIFICATION_TITLE_I18N_KEYS: Record<string, string> = buildTitleKeyMap();

/**
 * Localized title for a notification: the catalog title when the type resolves
 * to a single catalog event, otherwise the stored English title.
 */
export function localizedNotificationTitle(
  t: TFunction,
  notificationType: string | null | undefined,
  fallbackTitle: string,
): string {
  if (!notificationType) return fallbackTitle;
  const i18nKey = NOTIFICATION_TITLE_I18N_KEYS[notificationType];
  if (!i18nKey) return fallbackTitle;
  return t(i18nKey, { defaultValue: fallbackTitle });
}

/**
 * Most types a single query may resolve to. The catalog holds ~150; a query
 * matching more than this is generic enough that the server's own text arms
 * sweep the same rows, and the list travels in a URL.
 */
const MAX_MATCHED_TYPES = 25;

/**
 * The notification types whose LOCALIZED title matches `query`.
 *
 * The bell renders {@link localizedNotificationTitle}, not the stored English
 * `title`, so a reader searching their own language — or a tenant's renamed
 * terminology ("Opportunity" where the catalog says `{{Deal}}`) — is matching
 * text the `notifications` table does not hold. The server therefore cannot
 * resolve those rows on its own: only the client has the bundles and the
 * interpolation. It hands the resolved TYPES over instead, and the inbox route
 * admits them as subject matches (`search_types`).
 *
 * Admission is `searchScore(...) > 0`, deliberately the same bar
 * `filterAndSortByPrioritizedSearch` applies to a primary field — including its
 * typo and partial-match tiers — so this widens the candidate set by exactly
 * what the client would go on to accept, and never by less.
 */
export function notificationTypesMatchingQuery(
  t: TFunction,
  query: string,
  limit: number = MAX_MATCHED_TYPES,
): string[] {
  if (!query.trim()) return [];
  const scored: { type: string; score: number }[] = [];
  for (const [type, i18nKey] of Object.entries(NOTIFICATION_TITLE_I18N_KEYS)) {
    const label = t(i18nKey, { defaultValue: '' });
    if (!label) continue;
    const score = searchScore(label, query);
    if (score > 0) scored.push({ type, score });
  }
  // Strongest first, so the cap drops the weakest matches rather than an
  // arbitrary slice of the catalog's iteration order.
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((entry) => entry.type);
}
